import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../constants/appConstants';
import { useAuth } from '../../context/AuthContext';
import './EstimateReviewPage.css';

type ReviewMode = 'detection' | 'search';
type Severity = 'critical' | 'high' | 'medium' | 'low';
type DatePreset = '7' | '30' | '90' | 'all' | 'custom';
type QueueFilter = 'all' | 'critical' | 'high';
type ClarityInquiryType = 'question' | 'concern' | 'followup';

interface IssueFlag {
  type: string;
  severity: Severity;
  title: string;
  detail: string;
}

interface SimilarSummary {
  count: number;
  medianActualHours: number | null;
  averageActualHours?: number | null;
  actualToMedianMultiplier?: number | null;
}

interface SimilarTask {
  id: number;
  taskDescription: string;
  employeeName: string;
  actualHours: number;
  estimateHours: number;
  projectName: string;
  score: number;
}

interface ReviewTask {
  id: number;
  intranetTaskId?: string | null;
  mondayTaskId?: string | null;
  taskDescription: string;
  taskType: string;
  projectName: string | null;
  accountName: string | null;
  employeeId: string;
  employeeName: string;
  estimateHours: number;
  actualHours: number;
  estimateUsagePercent: number | null;
  completedAt: string | null;
  issues?: IssueFlag[];
  primaryIssue?: IssueFlag | null;
  issueScore?: number;
  similarTaskSummary?: SimilarSummary;
  similarTasks?: SimilarTask[];
  verdict?: string | null;
}

interface DeveloperProfile {
  employeeId: string;
  employeeName: string;
  flagCount: number;
  criticalCount: number;
  highCount: number;
  riskScore: number;
  riskLabel: string;
  patterns: string[];
}

interface TeamBriefAction {
  issueType?: string;
  employeeName?: string;
  taskDescription?: string;
  managerStep?: string;
  askDeveloper?: string;
}

interface TeamBrief {
  summary?: string;
  watchDevelopers?: string[];
  recommendedActions?: Array<string | TeamBriefAction>;
  confidence?: string;
}

function formatTeamBriefAction(action: string | TeamBriefAction): string {
  if (typeof action === 'string') {
    return action;
  }

  const parts = [
    action.managerStep,
    action.employeeName ? `(${action.employeeName})` : null,
    action.askDeveloper ? `Ask: ${action.askDeveloper}` : null
  ].filter(Boolean);

  return parts.join(' — ') || action.taskDescription || 'Review flagged task';
}

interface DashboardResponse {
  generatedAt: string;
  stats: {
    scanned: number;
    flagged: number;
    dismissedCount?: number;
    developersFlagged: number;
    criticalCount: number;
    highCount: number;
  };
  teamBrief: TeamBrief | null;
  developers: DeveloperProfile[];
  flaggedTasks: ReviewTask[];
}

interface EmployeeFilterOption {
  id: string;
  name: string;
  taskCount: number;
}

interface ReviewFilterOptions {
  employees: EmployeeFilterOption[];
  taskTypes: string[];
  accounts: string[];
  projects: string[];
}

const MULTIPLIER_OPTIONS = ['2', '3', '4', '5'];

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatHours = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '--';
  }

  return `${Number(value).toFixed(2)}h`;
};

const getSeverity = (value: string | undefined | null): Severity => {
  if (value === 'critical' || value === 'high' || value === 'medium') {
    return value;
  }

  return 'low';
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 0) {
    return '?';
  }

  return parts.map(part => part.charAt(0).toUpperCase()).join('');
};

const buildDefaultClarityMessage = (task: ReviewTask): string => {
  const peerMedian = task.similarTaskSummary?.medianActualHours;
  const multiplier = task.similarTaskSummary?.actualToMedianMultiplier;
  const parts = [
    `Can you add context on this task? It shows ${formatHours(task.actualHours)} actual against a ${formatHours(task.estimateHours)} estimate.`
  ];

  if (peerMedian && multiplier) {
    parts.push(`The peer median for similar work is ${formatHours(peerMedian)} (${multiplier}x difference).`);
  }

  if (task.primaryIssue?.detail) {
    parts.push(`Flagged reason: ${task.primaryIssue.detail}`);
  }

  parts.push('Please reply with what changed in scope, blockers, or testing/rework that explains the time.');

  return parts.join(' ');
};

const EstimateReviewPage: React.FC = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState<ReviewMode>('detection');
  const [multiplier, setMultiplier] = useState('2');
  const [datePreset, setDatePreset] = useState<DatePreset>('30');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [query, setQuery] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [taskType, setTaskType] = useState('');
  const [accountName, setAccountName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [filterOptions, setFilterOptions] = useState<ReviewFilterOptions>({
    employees: [],
    taskTypes: [],
    accounts: [],
    projects: []
  });
  const [searchResults, setSearchResults] = useState<ReviewTask[]>([]);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('all');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [showDismissForm, setShowDismissForm] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissingTaskId, setDismissingTaskId] = useState<number | null>(null);
  const [dismissMessage, setDismissMessage] = useState<string | null>(null);
  const [managerIntranetId, setManagerIntranetId] = useState('');
  const [clarityType, setClarityType] = useState<ClarityInquiryType>('question');
  const [clarityMessage, setClarityMessage] = useState('');
  const [sendingClarityTaskId, setSendingClarityTaskId] = useState<number | null>(null);
  const [clarityStatus, setClarityStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reviewFilterParams = useMemo(() => {
    const params: Record<string, string> = {};

    if (datePreset === 'custom') {
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
    } else if (datePreset !== 'all') {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - Number(datePreset));
      params.startDate = formatDateInput(start);
      params.endDate = formatDateInput(end);
    }

    if (employeeId.trim()) {
      params.employeeId = employeeId.trim();
    }
    if (taskType.trim()) {
      params.taskType = taskType.trim();
    }
    if (accountName.trim()) {
      params.accountName = accountName.trim();
    }
    if (projectName.trim()) {
      params.projectName = projectName.trim();
    }

    return params;
  }, [accountName, datePreset, employeeId, endDate, projectName, startDate, taskType]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get<ReviewFilterOptions>(
        `${API_URL}/task-estimates/review/filter-options`,
        {
          withCredentials: true
        }
      );

      setFilterOptions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load estimate review filters');
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoadingDashboard(true);
    setError(null);

    try {
      const response = await axios.get<DashboardResponse>(
        `${API_URL}/task-estimates/review/dashboard`,
        {
          params: {
            multiplier,
            ...reviewFilterParams,
            historyLimit: 120,
            limit: 40,
            includeAiBrief: '1'
          },
          withCredentials: true
        }
      );

      const nextDashboard = response.data;
      setDashboard(nextDashboard);
      setQueueFilter('all');
      setSelectedTaskId(nextDashboard.flaggedTasks[0]?.id ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to scan team estimate patterns');
    } finally {
      setLoadingDashboard(false);
    }
  }, [multiplier, reviewFilterParams]);

  const fetchSearchResults = useCallback(async () => {
    setLoadingSearch(true);
    setError(null);

    try {
      const response = await axios.get<{ tasks: ReviewTask[] }>(
        `${API_URL}/task-estimates/review/search`,
        {
          params: {
            query,
            ...reviewFilterParams,
            searchMode: query.trim() ? 'semantic' : 'filtered',
            candidateLimit: 750,
            limit: 40,
            enrich: '1',
            enrichLimit: 25
          },
          withCredentials: true
        }
      );

      const tasks = response.data.tasks || [];
      setHasSearched(true);
      setSearchResults(tasks);
      setSelectedTaskId(tasks[0]?.id ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to search estimate history');
    } finally {
      setLoadingSearch(false);
    }
  }, [query, reviewFilterParams]);

  useEffect(function loadDetectionDashboard() {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(function loadReviewFilterOptions() {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(function loadCurrentManager() {
    const fetchCurrentManager = async () => {
      try {
        const response = await axios.get<{ id: string; name: string }>(
          `${API_URL}/intranet/me`,
          { withCredentials: true }
        );

        setManagerIntranetId(String(response.data?.id || ''));
      } catch {
        setManagerIntranetId('');
      }
    };

    fetchCurrentManager();
  }, []);

  const handleSearchSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchSearchResults();
  }, [fetchSearchResults]);

  const flaggedTasks = useMemo(() => dashboard?.flaggedTasks ?? [], [dashboard]);

  const queueCounts = useMemo(() => ({
    all: flaggedTasks.length,
    critical: flaggedTasks.filter(task => getSeverity(task.primaryIssue?.severity) === 'critical').length,
    high: flaggedTasks.filter(task => getSeverity(task.primaryIssue?.severity) === 'high').length
  }), [flaggedTasks]);

  const visibleTasks = useMemo(() => {
    if (mode === 'search') {
      return searchResults;
    }

    return flaggedTasks.filter(task => {
      const severity = getSeverity(task.primaryIssue?.severity);

      if (queueFilter === 'critical') {
        return severity === 'critical';
      }

      if (queueFilter === 'high') {
        return severity === 'high';
      }

      return true;
    });
  }, [flaggedTasks, mode, queueFilter, searchResults]);

  const selectedTask = useMemo(
    () => visibleTasks.find(task => task.id === selectedTaskId) || visibleTasks[0] || null,
    [selectedTaskId, visibleTasks]
  );

  useEffect(function syncSelectedTaskWithQueue() {
    if (visibleTasks.length === 0) {
      if (selectedTaskId !== null) {
        setSelectedTaskId(null);
      }
      return;
    }

    if (!visibleTasks.some(task => task.id === selectedTaskId)) {
      setSelectedTaskId(visibleTasks[0].id);
    }
  }, [selectedTaskId, visibleTasks]);

  useEffect(function resetDismissFormForSelectedTask() {
    setShowDismissForm(false);
    setDismissReason('');
    setDismissMessage(null);
    setClarityStatus(null);
    setClarityType('question');

    if (selectedTask) {
      setClarityMessage(buildDefaultClarityMessage(selectedTask));
    } else {
      setClarityMessage('');
    }
  }, [selectedTask, selectedTaskId]);

  const handleQueueFilterChange = useCallback((filter: QueueFilter) => {
    setQueueFilter(filter);
  }, []);

  const handleDismissFlaggedTask = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTask) {
      return;
    }

    const reason = dismissReason.trim();
    if (reason.length < 8) {
      setError('Please enter a short reason explaining why this flag was incorrect.');
      return;
    }

    setDismissingTaskId(selectedTask.id);
    setError(null);
    setDismissMessage(null);

    try {
      await axios.post(
        `${API_URL}/task-estimates/review/${selectedTask.id}/dismiss`,
        { reason },
        { withCredentials: true }
      );

      setDismissReason('');
      setShowDismissForm(false);
      setDismissMessage('Flag dismissed. The task will no longer count toward the manager scoring queue.');
      await fetchDashboard();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to dismiss flagged task');
    } finally {
      setDismissingTaskId(null);
    }
  }, [dismissReason, fetchDashboard, selectedTask]);

  const handleSendClarityRequest = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTask) {
      return;
    }

    const message = clarityMessage.trim();
    if (message.length < 12) {
      setError('Please enter a specific question before sending the Slack clarity request.');
      return;
    }

    if (!managerIntranetId) {
      setError('Could not identify the current manager. Please refresh and try again.');
      return;
    }

    setSendingClarityTaskId(selectedTask.id);
    setError(null);
    setClarityStatus(null);

    try {
      await axios.post(
        `${API_URL}/task-inquiries`,
        {
          fromEmployeeId: managerIntranetId,
          fromEmployeeName: user?.username || 'Manager',
          toEmployeeId: selectedTask.employeeId,
          toEmployeeName: selectedTask.employeeName,
          intranetTaskId: selectedTask.intranetTaskId || String(selectedTask.id),
          taskDescription: selectedTask.taskDescription,
          taskProject: selectedTask.projectName || selectedTask.accountName || 'Unknown',
          taskType: selectedTask.taskType,
          taskTimeSeconds: Math.round(Number(selectedTask.actualHours || 0) * 3600),
          inquiryType: clarityType,
          message
        },
        { withCredentials: true }
      );

      setClarityStatus(`Slack clarity request sent to ${selectedTask.employeeName}.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send Slack clarity request');
    } finally {
      setSendingClarityTaskId(null);
    }
  }, [clarityMessage, clarityType, managerIntranetId, selectedTask, user?.username]);

  const stats = dashboard?.stats;
  const brief = dashboard?.teamBrief;

  const briefScanMetrics = useMemo(() => {
    if (!stats) {
      return null;
    }

    const flaggedRate = stats.scanned > 0
      ? Math.round((stats.flagged / stats.scanned) * 100)
      : 0;
    const mediumLowCount = Math.max(0, stats.flagged - stats.criticalCount - stats.highCount);

    return {
      flaggedRate,
      mediumLowCount,
      severityTotal: Math.max(stats.flagged, 1)
    };
  }, [stats]);

  const briefWatchDevelopers = useMemo(() => {
    if (!dashboard?.developers?.length) {
      return [];
    }

    const devByName = new Map(dashboard.developers.map(dev => [dev.employeeName, dev]));
    const watchNames = brief?.watchDevelopers || [];

    if (watchNames.length > 0) {
      return watchNames
        .map(name => devByName.get(name))
        .filter((dev): dev is DeveloperProfile => Boolean(dev));
    }

    return dashboard.developers.slice(0, 4);
  }, [brief?.watchDevelopers, dashboard?.developers]);

  const briefTopOutliers = useMemo(() => {
    if (!dashboard?.flaggedTasks?.length) {
      return [];
    }

    return [...dashboard.flaggedTasks]
      .sort((left, right) => {
        const leftScore = left.similarTaskSummary?.actualToMedianMultiplier || left.issueScore || 0;
        const rightScore = right.similarTaskSummary?.actualToMedianMultiplier || right.issueScore || 0;
        return rightScore - leftScore;
      })
      .slice(0, 3);
  }, [dashboard?.flaggedTasks]);

  const briefDeveloperMetrics = useMemo(() => {
    const peakMultipliers = new Map<string, number>();
    const maxRiskScore = Math.max(...(dashboard?.developers || []).map(dev => dev.riskScore), 1);
    const maxFlagCount = Math.max(...(dashboard?.developers || []).map(dev => dev.flagCount), 1);

    for (const task of dashboard?.flaggedTasks || []) {
      const multiplier = task.similarTaskSummary?.actualToMedianMultiplier;
      if (!multiplier) {
        continue;
      }

      peakMultipliers.set(
        task.employeeName,
        Math.max(peakMultipliers.get(task.employeeName) || 0, multiplier)
      );
    }

    return {
      maxRiskScore,
      maxFlagCount,
      peakMultipliers
    };
  }, [dashboard?.developers, dashboard?.flaggedTasks]);

  const renderTaskCard = (task: ReviewTask) => {
    const severity = getSeverity(task.primaryIssue?.severity);
    const multiplierValue = task.similarTaskSummary?.actualToMedianMultiplier;
    const isActive = selectedTask?.id === task.id;

    return (
      <button
        key={task.id}
        type="button"
        className={`ed-task-card ed-task-card-compact sev-${severity} ${isActive ? 'is-active' : ''}`}
        onClick={() => setSelectedTaskId(task.id)}
      >
        <span className="ed-task-rail" aria-hidden="true" />
        <div className="ed-task-body">
          <div className="ed-task-head">
            <span className="ed-avatar">{getInitials(task.employeeName)}</span>
            <div className="ed-task-head-text">
              <strong>{task.employeeName}</strong>
              <span>{task.accountName || task.projectName || task.taskType}</span>
            </div>
            {multiplierValue ? (
              <span className={`ed-multiplier sev-${severity}`}>{multiplierValue}x</span>
            ) : task.estimateUsagePercent ? (
              <span className="ed-usage">{task.estimateUsagePercent}%</span>
            ) : null}
          </div>
          <p className="ed-task-title">{task.taskDescription}</p>
          {task.primaryIssue && (
            <span className={`ed-chip sev-${severity}`}>{task.primaryIssue.title}</span>
          )}
        </div>
      </button>
    );
  };

  const renderTaskQueue = (title: string, emptyMessage: string) => (
    <aside className="ed-queue" aria-label={title}>
      <div className="ed-queue-head">
        <h2 className="ed-section-title">{title}</h2>
        {mode === 'detection' && (
          <div className="ed-queue-tabs" role="tablist" aria-label="Flag severity filters">
            <button
              type="button"
              role="tab"
              aria-selected={queueFilter === 'all'}
              className={queueFilter === 'all' ? 'is-active' : ''}
              onClick={() => handleQueueFilterChange('all')}
            >
              All <span className="ed-filter-count">{queueCounts.all}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={queueFilter === 'critical'}
              className={queueFilter === 'critical' ? 'is-active' : ''}
              onClick={() => handleQueueFilterChange('critical')}
            >
              Critical <span className="ed-filter-count">{queueCounts.critical}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={queueFilter === 'high'}
              className={queueFilter === 'high' ? 'is-active' : ''}
              onClick={() => handleQueueFilterChange('high')}
            >
              High <span className="ed-filter-count">{queueCounts.high}</span>
            </button>
          </div>
        )}
      </div>
      {visibleTasks.length === 0 ? (
        <div className="ed-queue-empty">{emptyMessage}</div>
      ) : (
        <div className="ed-queue-list">
          {visibleTasks.map(renderTaskCard)}
        </div>
      )}
    </aside>
  );

  const renderComparisonBar = (task: ReviewTask) => {
    const peerMedian = task.similarTaskSummary?.medianActualHours;
    const actual = task.actualHours;

    if (!peerMedian || peerMedian <= 0) {
      return (
        <div className="ed-bar ed-bar-empty">
          <span>No peer baseline yet for this kind of work</span>
        </div>
      );
    }

    const max = Math.max(actual, peerMedian);
    const actualPct = Math.max(6, Math.round((actual / max) * 100));
    const peerPct = Math.max(6, Math.round((peerMedian / max) * 100));

    return (
      <div className="ed-bar">
        <div className="ed-bar-row">
          <span className="ed-bar-label">This dev</span>
          <div className="ed-bar-track">
            <div className="ed-bar-fill ed-bar-fill-actual" style={{ width: `${actualPct}%` }} />
          </div>
          <span className="ed-bar-value">{formatHours(actual)}</span>
        </div>
        <div className="ed-bar-row">
          <span className="ed-bar-label">Peer median</span>
          <div className="ed-bar-track">
            <div className="ed-bar-fill ed-bar-fill-peer" style={{ width: `${peerPct}%` }} />
          </div>
          <span className="ed-bar-value">{formatHours(peerMedian)}</span>
        </div>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedTask) {
      return (
        <aside className="ed-detail ed-detail-empty">
          <div className="ed-detail-empty-inner">
            <span className="ed-detail-empty-icon">🔍</span>
            <strong>No investigation to show</strong>
            <p>Run a scan or search to compare a task against similar peer work.</p>
          </div>
        </aside>
      );
    }

    const issues = selectedTask.issues && selectedTask.issues.length > 0
      ? selectedTask.issues
      : selectedTask.primaryIssue
        ? [selectedTask.primaryIssue]
        : [];
    const similarTasks = selectedTask.similarTasks || [];
    const summary = selectedTask.similarTaskSummary;

    return (
      <aside className="ed-detail">
        <header className="ed-detail-head">
          <div className="ed-detail-title-block">
            <span className="ed-detail-eyebrow">Investigation</span>
            <h2>{selectedTask.taskDescription}</h2>
          </div>
          <div className="ed-detail-meta">
            <span className="ed-avatar">{getInitials(selectedTask.employeeName)}</span>
            <div>
              <strong>{selectedTask.employeeName}</strong>
              <span>{selectedTask.accountName || selectedTask.projectName || selectedTask.taskType}</span>
            </div>
          </div>
        </header>

        <div className="ed-detail-scroll">
          <div className="ed-detail-overview">
            {renderComparisonBar(selectedTask)}

            <div className="ed-detail-stats">
              <div>
                <span>Estimate</span>
                <strong>{formatHours(selectedTask.estimateHours)}</strong>
              </div>
              <div>
                <span>Actual</span>
                <strong>{formatHours(selectedTask.actualHours)}</strong>
              </div>
              <div>
                <span>Used</span>
                <strong>{selectedTask.estimateUsagePercent ? `${selectedTask.estimateUsagePercent}%` : '--'}</strong>
              </div>
              <div>
                <span>vs peers</span>
                <strong>{summary?.actualToMedianMultiplier ? `${summary.actualToMedianMultiplier}x` : '--'}</strong>
              </div>
            </div>
          </div>

          <div className="ed-detail-content-grid">
            <div className="ed-detail-column">
              {issues.length > 0 && (
                <section className="ed-detail-section">
                  <div className="ed-section-heading-row">
                    <h3>Why it was flagged</h3>
                    {mode === 'detection' && (
                      <button
                        type="button"
                        className="ed-dismiss-toggle"
                        onClick={() => setShowDismissForm(current => !current)}
                      >
                        {showDismissForm ? 'Cancel' : 'Dismiss flag'}
                      </button>
                    )}
                  </div>
                  <ul className="ed-issue-list">
                    {issues.map(issue => (
                      <li key={issue.type} className={`sev-${getSeverity(issue.severity)}`}>
                        <strong>{issue.title}</strong>
                        <span>{issue.detail}</span>
                      </li>
                    ))}
                  </ul>
                  {dismissMessage && (
                    <p className="ed-dismiss-message">{dismissMessage}</p>
                  )}
                  {mode === 'detection' && showDismissForm && (
                    <form className="ed-dismiss-form" onSubmit={handleDismissFlaggedTask}>
                      <label>
                        <span>Why was this incorrectly flagged?</span>
                        <textarea
                          value={dismissReason}
                          onChange={event => setDismissReason(event.target.value)}
                          placeholder="Example: Actual scope included untracked client rework, so this should not count against the employee."
                          rows={4}
                        />
                      </label>
                      <button
                        type="submit"
                        className="ed-dismiss-submit"
                        disabled={dismissingTaskId === selectedTask.id}
                      >
                        {dismissingTaskId === selectedTask.id ? 'Dismissing…' : 'Dismiss and remove from scoring'}
                      </button>
                    </form>
                  )}
                </section>
              )}

              <section className="ed-detail-section ed-clarity-section">
                <div className="ed-section-heading-row">
                  <div>
                    <h3>Ask for clarity</h3>
                    <p className="ed-muted">
                      Send a Slack group DM to {selectedTask.employeeName} and managers with this task context.
                    </p>
                  </div>
                </div>
                <form className="ed-clarity-form" onSubmit={handleSendClarityRequest}>
                  <label>
                    <span>Type</span>
                    <select
                      value={clarityType}
                      onChange={event => setClarityType(event.target.value as ClarityInquiryType)}
                    >
                      <option value="question">Question</option>
                      <option value="concern">Concern</option>
                      <option value="followup">Follow-up</option>
                    </select>
                  </label>
                  <label>
                    <span>Message</span>
                    <textarea
                      value={clarityMessage}
                      onChange={event => setClarityMessage(event.target.value)}
                      rows={5}
                      placeholder="Ask what changed in scope, what blockers came up, or what testing/rework explains the time."
                    />
                  </label>
                  <div className="ed-clarity-actions">
                    <button
                      type="button"
                      className="ed-dismiss-toggle"
                      onClick={() => setClarityMessage(buildDefaultClarityMessage(selectedTask))}
                    >
                      Reset suggested message
                    </button>
                    <button
                      type="submit"
                      className="ed-dismiss-submit"
                      disabled={sendingClarityTaskId === selectedTask.id || !clarityMessage.trim()}
                    >
                      {sendingClarityTaskId === selectedTask.id ? 'Sending...' : 'Send Slack clarity request'}
                    </button>
                  </div>
                </form>
                {clarityStatus && (
                  <p className="ed-dismiss-message">{clarityStatus}</p>
                )}
              </section>
            </div>

            <section className="ed-detail-section">
              <h3>Similar work by peers {summary?.count ? `(${summary.count})` : ''}</h3>
              {similarTasks.length === 0 ? (
                <p className="ed-muted">
                  No strong peer matches found yet. More comparisons appear as similar tasks complete and get embedded.
                </p>
              ) : (
                <div className="ed-similar-list">
                  {similarTasks.map(similar => (
                    <article key={similar.id} className="ed-similar-item">
                      <div className="ed-similar-top">
                        <strong>{similar.employeeName}</strong>
                        <span>{formatHours(similar.actualHours)} actual</span>
                      </div>
                      <p>{similar.taskDescription}</p>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </aside>
    );
  };

  if (loadingDashboard && !dashboard) {
    return (
      <div className="ed-page">
        <div className="ed-loading">
          <div className="ed-loading-orb" />
          <strong>Scanning team estimate patterns…</strong>
          <p>Comparing recently completed work against similar peer tasks to surface who needs a closer look.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ed-page">
      <header className="ed-header">
        <div className="ed-header-text">
          <span className="ed-eyebrow">Estimate Intelligence</span>
          <h1>Estimate Detection</h1>
          <p>Spot developers who are struggling, running far over similar peer work, or burning through high estimates.</p>
        </div>
        <div className="ed-header-controls">
          <div className="ed-tabs" role="tablist" aria-label="Estimate review modes">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'detection'}
              className={mode === 'detection' ? 'is-active' : ''}
              onClick={() => setMode('detection')}
            >
              Detection
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'search'}
              className={mode === 'search' ? 'is-active' : ''}
              onClick={() => {
                setMode('search');
                if (!hasSearched) {
                  fetchSearchResults();
                }
              }}
            >
              Search
            </button>
          </div>
          {mode === 'detection' && (
            <div className="ed-control-group">
              <label className="ed-inline-field">
                <span>Threshold</span>
                <select value={multiplier} onChange={event => setMultiplier(event.target.value)}>
                  {MULTIPLIER_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}x median</option>
                  ))}
                </select>
              </label>
              <button type="button" className="ed-primary" onClick={fetchDashboard} disabled={loadingDashboard}>
                {loadingDashboard ? 'Scanning…' : 'Rescan'}
              </button>
            </div>
          )}
        </div>
      </header>

      {error && <div className="ed-error">{error}</div>}

      <section className="ed-review-filters" aria-label="Estimate review filters">
        <label className="ed-field">
          <span>Review window</span>
          <select value={datePreset} onChange={event => setDatePreset(event.target.value as DatePreset)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom range</option>
          </select>
        </label>
        {datePreset === 'custom' && (
          <>
            <label className="ed-field">
              <span>From</span>
              <input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
            </label>
            <label className="ed-field">
              <span>To</span>
              <input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
            </label>
          </>
        )}
        <label className="ed-field">
          <span>Employee</span>
          <select
            value={employeeId}
            onChange={event => setEmployeeId(event.target.value)}
          >
            <option value="">All employees</option>
            {filterOptions.employees.map(employee => (
              <option key={employee.id} value={employee.id}>
                {employee.name} ({employee.taskCount})
              </option>
            ))}
          </select>
        </label>
        <label className="ed-field">
          <span>Task type</span>
          <select
            value={taskType}
            onChange={event => setTaskType(event.target.value)}
          >
            <option value="">All task types</option>
            {filterOptions.taskTypes.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <label className="ed-field">
          <span>Account</span>
          <select
            value={accountName}
            onChange={event => setAccountName(event.target.value)}
          >
            <option value="">All accounts</option>
            {filterOptions.accounts.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
        <label className="ed-field">
          <span>Project</span>
          <select
            value={projectName}
            onChange={event => setProjectName(event.target.value)}
          >
            <option value="">All projects</option>
            {filterOptions.projects.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
      </section>

      {mode === 'detection' && dashboard && (
        <>
          <section className="ed-kpis">
            <div className="ed-kpi">
              <span className="ed-kpi-label">Tasks scanned</span>
              <strong>{stats?.scanned ?? 0}</strong>
              <span className="ed-kpi-sub">
                {stats?.dismissedCount ? `${stats.dismissedCount} dismissed hidden` : 'recent completed'}
              </span>
            </div>
            <button
              type="button"
              className={`ed-kpi ed-kpi-button ${queueFilter === 'all' ? 'ed-kpi-active' : ''}`}
              onClick={() => handleQueueFilterChange('all')}
            >
              <span className="ed-kpi-label">Flagged work</span>
              <strong>{stats?.flagged ?? 0}</strong>
              <span className="ed-kpi-sub">click to view queue</span>
            </button>
            <div className="ed-kpi">
              <span className="ed-kpi-label">Developers</span>
              <strong>{stats?.developersFlagged ?? 0}</strong>
              <span className="ed-kpi-sub">with flags</span>
            </div>
            <button
              type="button"
              className={`ed-kpi ed-kpi-alert ed-kpi-button ${queueFilter === 'critical' ? 'ed-kpi-active' : ''}`}
              onClick={() => handleQueueFilterChange('critical')}
            >
              <span className="ed-kpi-label">Critical</span>
              <strong>{stats?.criticalCount ?? 0}</strong>
              <span className="ed-kpi-sub">click to view critical</span>
            </button>
            <button
              type="button"
              className={`ed-kpi ed-kpi-button ${queueFilter === 'high' ? 'ed-kpi-active' : ''}`}
              onClick={() => handleQueueFilterChange('high')}
            >
              <span className="ed-kpi-label">High</span>
              <strong>{stats?.highCount ?? 0}</strong>
              <span className="ed-kpi-sub">click to view high</span>
            </button>
          </section>

          {brief ? (
            <section className="ed-brief">
              <div className="ed-brief-head">
                <span className="ed-brief-badge">AI Team Brief</span>
                {brief.confidence && (
                  <span className="ed-brief-confidence">{brief.confidence} confidence</span>
                )}
              </div>

              <div className="ed-brief-top">
                {briefScanMetrics && (
                  <div className="ed-brief-metrics">
                    <div className="ed-brief-metric">
                      <span className="ed-brief-metric-label">Flag rate</span>
                      <strong className="ed-brief-metric-value">{briefScanMetrics.flaggedRate}%</strong>
                      <div className="ed-brief-meter" aria-hidden="true">
                        <div
                          className="ed-brief-meter-fill"
                          style={{ width: `${Math.max(briefScanMetrics.flaggedRate, 4)}%` }}
                        />
                      </div>
                      <span className="ed-brief-metric-sub">
                        {stats?.flagged ?? 0} flagged of {stats?.scanned ?? 0} scanned
                      </span>
                    </div>

                    <div className="ed-brief-metric">
                      <span className="ed-brief-metric-label">Severity mix</span>
                      <div className="ed-brief-severity-stack" aria-hidden="true">
                        {(stats?.criticalCount ?? 0) > 0 && (
                          <div
                            className="ed-brief-severity-segment is-critical"
                            style={{ flex: stats?.criticalCount ?? 0 }}
                            title={`${stats?.criticalCount ?? 0} critical`}
                          />
                        )}
                        {(stats?.highCount ?? 0) > 0 && (
                          <div
                            className="ed-brief-severity-segment is-high"
                            style={{ flex: stats?.highCount ?? 0 }}
                            title={`${stats?.highCount ?? 0} high`}
                          />
                        )}
                        {briefScanMetrics.mediumLowCount > 0 && (
                          <div
                            className="ed-brief-severity-segment is-medium"
                            style={{ flex: briefScanMetrics.mediumLowCount }}
                            title={`${briefScanMetrics.mediumLowCount} medium/low`}
                          />
                        )}
                      </div>
                      <div className="ed-brief-severity-legend">
                        <span><i className="is-critical" /> {stats?.criticalCount ?? 0} critical</span>
                        <span><i className="is-high" /> {stats?.highCount ?? 0} high</span>
                        <span><i className="is-medium" /> {briefScanMetrics.mediumLowCount} other</span>
                      </div>
                    </div>

                    <div className="ed-brief-metric">
                      <span className="ed-brief-metric-label">Developers flagged</span>
                      <strong className="ed-brief-metric-value">{stats?.developersFlagged ?? 0}</strong>
                      <div className="ed-brief-meter ed-brief-meter-devs" aria-hidden="true">
                        {dashboard?.developers.slice(0, 6).map(dev => (
                          <div
                            key={dev.employeeId}
                            className="ed-brief-dev-segment"
                            style={{
                              flex: Math.max(dev.flagCount, 1),
                              opacity: Math.max(0.35, dev.riskScore / briefDeveloperMetrics.maxRiskScore)
                            }}
                            title={`${dev.employeeName}: ${dev.flagCount} flag(s)`}
                          />
                        ))}
                      </div>
                      <span className="ed-brief-metric-sub">
                        {briefWatchDevelopers.length} need closest review
                      </span>
                    </div>
                  </div>
                )}

                {brief.summary && (
                  <p className="ed-brief-summary">{brief.summary}</p>
                )}
              </div>

              {(briefWatchDevelopers.length > 0 || briefTopOutliers.length > 0) && (
                <div className="ed-brief-visuals">
                  {briefWatchDevelopers.length > 0 && (
                    <div className="ed-brief-panel">
                      <h4>Watch closely</h4>
                      <div className="ed-brief-dev-grid">
                        {briefWatchDevelopers.map(dev => {
                          const peakMultiplier = briefDeveloperMetrics.peakMultipliers.get(dev.employeeName);
                          const riskPct = Math.max(
                            8,
                            Math.round((dev.riskScore / briefDeveloperMetrics.maxRiskScore) * 100)
                          );
                          const flagPct = Math.max(
                            8,
                            Math.round((dev.flagCount / briefDeveloperMetrics.maxFlagCount) * 100)
                          );

                          return (
                            <div key={dev.employeeId} className="ed-brief-dev-card">
                              <div className="ed-brief-dev-head">
                                <span className="ed-avatar">{getInitials(dev.employeeName)}</span>
                                <div className="ed-brief-dev-meta">
                                  <strong>{dev.employeeName}</strong>
                                  <span>{dev.riskLabel}</span>
                                </div>
                                {peakMultiplier ? (
                                  <span className="ed-brief-dev-mult">{peakMultiplier}x peer</span>
                                ) : null}
                              </div>

                              <div className="ed-brief-dev-bars">
                                <div className="ed-brief-dev-bar-row">
                                  <span>Risk</span>
                                  <div className="ed-brief-dev-track">
                                    <div
                                      className="ed-brief-dev-fill is-risk"
                                      style={{ width: `${riskPct}%` }}
                                    />
                                  </div>
                                  <em>{dev.riskScore}</em>
                                </div>
                                <div className="ed-brief-dev-bar-row">
                                  <span>Flags</span>
                                  <div className="ed-brief-dev-track">
                                    <div
                                      className="ed-brief-dev-fill is-flags"
                                      style={{ width: `${flagPct}%` }}
                                    />
                                  </div>
                                  <em>{dev.flagCount}</em>
                                </div>
                              </div>

                              <div className="ed-brief-dev-tags">
                                {dev.criticalCount > 0 && (
                                  <span className="ed-brief-tag is-critical">{dev.criticalCount} critical</span>
                                )}
                                {dev.highCount > 0 && (
                                  <span className="ed-brief-tag is-high">{dev.highCount} high</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {briefTopOutliers.length > 0 && (
                    <div className="ed-brief-panel">
                      <h4>Top outliers vs peers</h4>
                      <div className="ed-brief-outlier-grid">
                        {briefTopOutliers.map(task => {
                          const severity = getSeverity(task.primaryIssue?.severity);
                          const multiplier = task.similarTaskSummary?.actualToMedianMultiplier;

                          return (
                            <button
                              key={task.id}
                              type="button"
                              className={`ed-brief-outlier-card sev-${severity} ${selectedTaskId === task.id ? 'is-active' : ''}`}
                              onClick={() => setSelectedTaskId(task.id)}
                            >
                              <div className="ed-brief-outlier-head">
                                <strong>{task.employeeName}</strong>
                                {multiplier ? (
                                  <span className="ed-brief-outlier-mult">{multiplier}x</span>
                                ) : null}
                              </div>
                              <p>{task.taskDescription}</p>
                              {renderComparisonBar(task)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {brief.recommendedActions && brief.recommendedActions.length > 0 && (
                <div className="ed-brief-actions-panel">
                  <h4>Recommended actions</h4>
                  <div className="ed-brief-actions-grid">
                    {brief.recommendedActions.map((action, index) => {
                      const isStructured = typeof action !== 'string';
                      const issueType = isStructured ? action.issueType : null;
                      const employeeName = isStructured ? action.employeeName : null;
                      const managerStep = isStructured
                        ? action.managerStep
                        : action;
                      const askDeveloper = isStructured ? action.askDeveloper : null;

                      return (
                        <div key={`${formatTeamBriefAction(action)}-${index}`} className="ed-brief-action-card">
                          <div className="ed-brief-action-head">
                            {issueType && (
                              <span className="ed-brief-action-type">{issueType.replace(/_/g, ' ')}</span>
                            )}
                            {employeeName && (
                              <span className="ed-brief-action-who">{employeeName}</span>
                            )}
                          </div>
                          <p>{managerStep}</p>
                          {askDeveloper && (
                            <span className="ed-brief-action-ask">Ask: {askDeveloper}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          ) : (
            <section className="ed-brief ed-brief-empty">
              <span className="ed-brief-badge">AI Team Brief</span>
              <p className="ed-brief-summary">
                No team-level concerns surfaced at the current threshold. Lower the threshold or rescan after more work completes.
              </p>
            </section>
          )}

          <div className="ed-main">
            {renderTaskQueue(
              'Flagged work',
              'No flagged tasks matched the current filters. Try a wider review window or lower threshold.'
            )}
            {renderDetailPanel()}
          </div>
        </>
      )}

      {mode === 'search' && (
        <>
          <form className="ed-search" onSubmit={handleSearchSubmit}>
            <label className="ed-field grow">
              <span>AI task search</span>
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Example: cleanup videos, CMS meeting, QuickBooks deploy"
              />
            </label>
            <button type="submit" className="ed-primary ed-search-submit" disabled={loadingSearch}>
              {loadingSearch ? 'Searching…' : 'Search'}
            </button>
          </form>

          <div className="ed-main">
            {renderTaskQueue(
              `Results${searchResults.length > 0 ? ` (${searchResults.length})` : ''}`,
              hasSearched
                ? 'No matching tasks found. Try a broader search term.'
                : 'Search to compare any task against similar peer work.'
            )}
            {renderDetailPanel()}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(EstimateReviewPage);
